import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ShareIcon from '@mui/icons-material/Share';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import StarIcon from '@mui/icons-material/Star';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
  Box,
  Typography,
  Button,
  Grid,
  IconButton,
  Tabs,
  Tab,
  Paper,
  Avatar,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import Imagezoomer from "../../Components/Imagezoomer/Imagezoomer"
import { getData, postData , patchData} from "../../Webservice/Webservice";
import { useCart } from "../../context/CartContext";

const rubikfont = {
  fontFamily: "Rubik, sans-serif",
};

const ProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [productData, setProductData] = useState(null);
  const [isInCart, setIsInCart] = useState(false);
  const [isInWishlist, setIsInWishlist] = useState(false);
  const [similarProducts, setSimilarProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState('description');
  const [selectedImage, setSelectedImage] = useState(0);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const { fetchCartCount } = useCart();

  useEffect(() => {
    const fetchProductDetails = async () => {
      setLoading(true);
      try {
        const tokenValue = sessionStorage.getItem("token");
        const token = tokenValue ? `Bearer ${tokenValue}` : "";
        const response = await getData(`product/view/${id}`,null, token);
        if (response && response.product) {
          setProductData(response.product);
          setSimilarProducts(response.similar?.productsSimilar || []);
          setIsInCart(response.cart || false);
          setIsInWishlist(response.wishlist || false);
          // Reset selected image when product changes
          setSelectedImage(0);
          // Reset quantity when a new product is loaded and ensure it's a valid number
          setQuantity(1);
        }
      } catch (error) {
        console.error("Error fetching product details:", error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchProductDetails();
    }
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: '#ffffff', ...rubikfont }}>
        <Box sx={{ maxWidth: '1400px', margin: '0 auto', padding: '40px 24px' }}>
          <Grid container spacing={{ xs: 4, lg: 10 }} alignItems="flex-start">
            {/* Image Section Skeleton */}
            <Grid item xs={12} lg={6}>
              {/* Main Image Skeleton */}
              <Box
                sx={{
                  width: {
                    xs: 260,
                    sm: 340,
                    md: 450,
                    lg: 520,
                  },
                  aspectRatio: "4 / 3",
                  bgcolor: "#f5f7fa",
                  borderRadius: 3,
                  p: { xs: 1.5, sm: 2 },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: { xs: "auto", md: 0 },
                  boxShadow: "0 10px 30px rgba(15, 23, 42, 0.18)",
                  border: "1px solid #e0e7ff",
                }}
              >
                <Skeleton variant="rectangular" width="100%" height="100%" />
              </Box>

              {/* Thumbnails Skeleton */}
              <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: { xs: 'center', sm: 'flex-start' }, mt: 3 }}>
                {[1, 2, 3, 4].map((idx) => (
                  <Skeleton key={idx} variant="rectangular" width={70} height={70} sx={{ borderRadius: 1 }} />
                ))}
              </Box>
            </Grid>

            {/* Details Section Skeleton */}
            <Grid item xs={12} lg={6}>
              <Box
                sx={{
                  p: { xs: 0, sm: 2, md: 3 },
                  width: "100%",
                  maxWidth: { xs: "100%", md: 550 },
                  mx: { xs: 0, md: "auto" },
                }}
              >
                {/* Top row: category + action icons */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, mb: 1.5 }}>
                  <Skeleton variant="text" width="30%" height={16} />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Skeleton variant="circular" width={36} height={36} />
                    <Skeleton variant="circular" width={36} height={36} />
                  </Box>
                </Box>

                {/* Product Title */}
                <Skeleton variant="text" width="90%" height={42} sx={{ mb: 2 }} />

                {/* Rating & Reviews */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3, pb: 2, borderBottom: "1px solid #f0f0f0" }}>
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Skeleton key={star} variant="rectangular" width={18} height={18} sx={{ borderRadius: "50%" }} />
                    ))}
                  </Box>
                  <Skeleton variant="text" width={40} height={15} />
                  <Skeleton variant="text" width={80} height={15} />
                </Box>

                {/* Price Section */}
                <Box sx={{ mb: 4 }}>
                  <Box sx={{ display: "flex", alignItems: "baseline", gap: 2, mb: 1, flexDirection: { xs: "column", sm: "row" } }}>
                    <Skeleton variant="text" width={150} height={48} />
                    <Skeleton variant="text" width={100} height={22} />
                  </Box>
                  <Skeleton variant="rectangular" width={200} height={24} sx={{ borderRadius: "4px" }} />
                </Box>

                {/* Stock & Quantity */}
                <Box sx={{ mb: 4 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <Skeleton variant="circular" width={8} height={8} />
                    <Skeleton variant="text" width={80} height={14} />
                  </Box>

                  {/* Quantity Selector */}
                  <Box sx={{ mb: 3 }}>
                    <Skeleton variant="text" width={60} height={13} sx={{ mb: 1 }} />
                    <Box sx={{ display: "inline-flex", alignItems: "center" }}>
                      <Skeleton variant="rectangular" width={44} height={44} />
                      <Skeleton variant="rectangular" width={80} height={44} sx={{ mx: 0.5 }} />
                      <Skeleton variant="rectangular" width={44} height={44} />
                    </Box>
                  </Box>

                  {/* Action Buttons */}
                  <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                    <Skeleton variant="rectangular" width={200} height={56} sx={{ borderRadius: "4px", flex: 1 }} />
                    <Skeleton variant="rectangular" width={200} height={56} sx={{ borderRadius: "4px", flex: 1 }} />
                  </Box>
                </Box>

                {/* Benefits */}
                <Grid container spacing={2} sx={{ pt: 3, borderTop: "1px solid #f0f0f0" }}>
                  {[1, 2, 3].map((item) => (
                    <Grid item xs={12} sm={6} md={4} key={item} sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                      <Skeleton variant="circular" width={20} height={20} />
                      <Box sx={{ flex: 1 }}>
                        <Skeleton variant="text" width="80%" height={13} />
                        <Skeleton variant="text" width="60%" height={12} sx={{ mt: 0.5 }} />
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Grid>
          </Grid>

          {/* Tabs Section Skeleton */}
          <Box sx={{ mt: 10, px: { xs: 2, sm: 3, md: 4 } }}>
            <Box sx={{ borderBottom: "1px solid #e0e0e0", mb: 5 }}>
              <Box sx={{ display: "flex", gap: 3, borderBottom: "2px solid #000", pb: 1 }}>
                {['Description', 'Key Features', 'How to Use', 'Product Details', 'Reviews'].map((tab) => (
                  <Skeleton key={tab} variant="rectangular" width={100} height={20} sx={{ borderRadius: "4px" }} />
                ))}
              </Box>
            </Box>

            <Box sx={{ maxWidth: "900px", mx: "auto" }}>
              {/* Description Skeleton */}
              <Box>
                <Skeleton variant="text" width="60%" height={24} sx={{ mb: 2 }} />
                {[1, 2, 3, 4].map((line) => (
                  <Skeleton key={line} variant="text" width="100%" height={16} sx={{ mb: 1 }} />
                ))}
              </Box>
            </Box>
          </Box>

          {/* Similar Products Skeleton */}
          <Box sx={{ mt: 8 }}>
            <Skeleton variant="text" width="40%" height={24} sx={{ mb: 3 }} />
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(2, 1fr)",
                  sm: "repeat(3, 1fr)",
                  md: "repeat(5, 1fr)",
                },
                gap: { xs: 2, sm: 3, md: 4 },
                minHeight: "300px",
              }}
            >
              {[1, 2, 3, 4, 5].map((item) => (
                <Box
                  key={item}
                  sx={{
                    bgcolor: "white",
                    borderRadius: 2,
                    overflow: "hidden",
                    border: "1px solid #f0f0f0",
                  }}
                >
                  <Box sx={{ position: "relative", paddingTop: "90%", bgcolor: "#f8f8f8" }}>
                    <Skeleton variant="rectangular" sx={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }} />
                  </Box>
                  <Box sx={{ p: { xs: 1, sm: 1.5, md: 2 } }}>
                    <Skeleton variant="text" width="90%" height={18} />
                    <Skeleton variant="text" width={60} height={16} sx={{ mt: 1 }} />
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  if (!productData) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Typography variant="h5">Product not found</Typography>
      </Box>
    );
  }

  const images = productData.product_images || [];
  const displayImage = images.length > 0 ? images[selectedImage] : "https://via.placeholder.com/450";

  const tabs = [
    { id: 'description', label: 'Description' },
    { id: 'features', label: 'Key Features' },
    { id: 'howto', label: 'How to Use' },
    { id: 'details', label: 'Product Details' },
    { id: 'reviews', label: `Reviews (${productData.totalReviews || 0})` }
  ];

  // Calculate discount percentage if not provided
  const calculateDiscount = () => {
    if (productData.b2b_discountValue) return productData.b2b_discountValue;
    if (productData.b2c_mrp && productData.b2b_sellingPrice) {
      return Math.round(((productData.b2c_mrp - productData.b2b_sellingPrice) / productData.b2c_mrp) * 100);
    }
    return 0;
  };

  const discount = calculateDiscount();

  const handleAddToCart = async () => {
    const tokenValue = sessionStorage.getItem("token");
    const token = tokenValue ? `Bearer ${tokenValue}` : "";

    if (!token) {
      setAuthModalOpen(true);
      return;
    }

    try {
      const qty = Math.max(1, Math.floor(Number(quantity) || 1));
      if (isInCart) {
        // Update cart quantity
        await patchData('cart/update-cart', {
          product_id: productData._id,
          quantity: qty
        }, token);
        await fetchCartCount();
        alert("Cart updated successfully!");
      } else {
        // Add to cart
        await postData('cart/add-to-cart', {
          product_id: productData._id,
          quantity: qty
        }, token);
        await fetchCartCount();
        setIsInCart(true);
        alert("Added to cart successfully!");
      }
    } catch (error) {
      console.error("Error updating cart:", error);
    }
  };

  const handleBuyNow = async () => {
    const tokenValue = sessionStorage.getItem("token");
    const token = tokenValue ? `Bearer ${tokenValue}` : "";

    if (!token) {
      setAuthModalOpen(true);
      return;
    }

    try {
      const qty = Math.max(1, Math.floor(Number(quantity) || 1));
      const payload = {
        productId: productData._id,
        quantity: qty
      };

      const response = await postData('order/buy-now/checkout', payload, token);

      if (response && response.checkout) {
        navigate('/checkout', { state: { checkoutData: response } });
      } else {
        alert(response?.message || "Failed to proceed to checkout.");
      }
    } catch (error) {
      console.error("Error processing buy now:", error);
      alert("An error occurred. Please try again.");
    }
  };

  const handleToggleWishlist = async () => {
    const tokenValue = sessionStorage.getItem("token");
    const token = tokenValue ? `Bearer ${tokenValue}` : "";

    if (!token) {
      setAuthModalOpen(true);
      return;
    }

    const user = JSON.parse(sessionStorage.getItem("user"));
    const customerId = user?._id;

    if (!customerId) {
      console.error("User ID not found");
      return;
    }

    try {
      const payload = {
        customer_id: customerId,
        product_id: productData._id
      };

      const endpoint = isInWishlist ? 'wishlist/remove' : 'wishlist/add';
      const response = await postData(endpoint, payload, token);

      if (response) {
        setIsInWishlist(!isInWishlist);
        console.log(isInWishlist ? "Removed from wishlist" : "Added to wishlist");
      }
    } catch (error) {
      console.error("Error updating wishlist:", error);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#ffffff', ...rubikfont }}>

      {/* Main Content */}
      <Box sx={{ maxWidth: '1400px', margin: '0 auto', padding: '40px 24px' }}>
        <Grid container spacing={{ xs: 4, lg: 10 }} alignItems="flex-start">
          {/* Image Section */}
          <Grid item xs={12} lg={6}>
          {/* Main Image */}
<Box
  sx={{
    flex: 1,
    order: { xs: 1, md: 2 },

    /* 👉 Fixed responsive width */
    width: {
      xs: 260,   // Mobile
      sm: 340,   // Small screens
      md: 450,   // Tablets & small laptops
      lg: 520,   // Large screens
    },

    /* 👉 Maintain proper shape */
    aspectRatio: "4 / 3",

    bgcolor: "linear-gradient(135deg, #f5f7fa 0%, #e4ecf7 100%)",
    borderRadius: 3,
    p: { xs: 1.5, sm: 2 },
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    mx: { xs: "auto", md: 0 }, // center only on mobile
    boxShadow: "0 10px 30px rgba(15, 23, 42, 0.18)",
    border: "1px solid #e0e7ff",
    position: "relative",
    overflow: "hidden",
  }}
>

  <Imagezoomer
    src={displayImage}
    alt={productData?.title || "Product image"}
    style={{
      width: "100%",
      height: "100%",
      objectFit: "contain",
    }}
  />
</Box>


            {/* Thumbnails */}
            <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: { xs: 'center', sm: 'flex-start' }, mt:3 }}>
              {images.map((img, idx) => (
                <Button
                  key={idx}
                  onClick={() => setSelectedImage(idx)}
                  sx={{
                    minWidth: 70,
                    height: 70,
                    bgcolor: '#fafafa',
                    border: selectedImage === idx ? '2px solid #000' : '1px solid #e0e0e0',
                    p: 1,
                    transition: 'all 0.2s',
                  }}
                >
                  <img src={img} alt={`Thumbnail ${idx + 1}`} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                </Button>
              ))}
            </Box>
          </Grid>

          {/* Details Section */}
          <Grid item xs={12} lg={6}>
            <Box
              sx={{
                p: { xs: 0, sm: 2, md: 3 },
                width: "100%",
                maxWidth: { xs: "100%", md: 550 },
                mx: { xs: 0, md: "auto" },
              }}
            >
              {/* Top row: category + action icons */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 2,
                  mb: 1.5,
                }}
              >
                {/* category / subcategory */}
                <Typography
                  variant="caption"
                  sx={{
                    textTransform: "uppercase",
                    letterSpacing: "1.5px",
                    color: "#999",
                    fontWeight: 500,
                    ...rubikfont,
                  }}
                >
                  {productData.category_id?.title} / {productData.subcategory_id?.title}
                </Typography>

                {/* Favorite + Share icons (top-right of details) */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <IconButton
                    onClick={handleToggleWishlist}
                    sx={{ border: '1px solid #e0e0e0', borderRadius: '50%', p: '8px' }}
                  >
                    {isInWishlist
                      ? <FavoriteIcon sx={{ fontSize: 20, color: '#f80000ff' }} />
                      : <FavoriteBorderIcon sx={{ fontSize: 20 }} />}
                  </IconButton>
                  <IconButton
                    sx={{ border: '1px solid #e0e0e0', borderRadius: '50%', p: '8px' }}
                    onClick={() => {
                      if (navigator.share) {
                        navigator.share({
                          title: "Product",
                          text: "Check out this product!",
                          url: window.location.href
                        })
                          .then(() => console.log("Shared successfully"))
                          .catch(err => console.error("Share failed:", err));
                      } else {
                        alert("Share not supported on this device");
                      }
                    }}
                  >
                    <ShareIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Box>
              </Box>

              {/* Product Title */}
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: "#000",
                  fontSize: { xs: "26px", sm: "32px", md: "42px" },
                  mb: 2,
                  lineHeight: 1.2,
                  ...rubikfont,
                }}
              >
                {productData.title}
              </Typography>

              {/* Rating & Reviews */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  mb: 3,
                  pb: 2,
                  borderBottom: "1px solid #f0f0f0",
                  flexWrap: "wrap",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <StarIcon
                      key={star}
                      sx={{
                        fontSize: "18px",
                        color: star <= Number(productData.productRating || 0) ? "#FFB800" : "#e0e0e0",
                      }}
                    />
                  ))}
                </Box>
                <Typography sx={{ fontSize: "15px", color: "#666", fontWeight: 500, ...rubikfont }}>
                  {productData.productRating || "0.0"}
                </Typography>
                <Typography sx={{ color: "#999" }}>|</Typography>
                <Typography sx={{ fontSize: "15px", color: "#666", ...rubikfont }}>
                  {productData.totalReviews || 0} Reviews
                </Typography>
              </Box>

              {/* Price Section */}
              <Box sx={{ mb: 4 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: 2,
                    mb: 1,
                    flexDirection: { xs: "column", sm: "row" },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: { xs: "36px", sm: "42px", md: "48px" },
                      fontWeight: 700,
                      color: "#000",
                      ...rubikfont,
                    }}
                  >
                    ₹{(productData.b2b_sellingPrice || 0).toLocaleString()}
                  </Typography>
                  {productData.b2c_mrp && productData.b2c_mrp > productData.b2b_sellingPrice && (
                    <Typography
                      sx={{
                        fontSize: { xs: "18px", sm: "22px" },
                        color: "red",
                        textDecoration: "line-through",
                        textDecorationColor: "red",
                        ...rubikfont,
                      }}
                    >
                      ₹{productData.b2c_mrp.toLocaleString()}
                    </Typography>
                  )}
                </Box>
                {discount > 0 && (
                  <Typography
                    sx={{
                      display: "inline-block",
                      backgroundColor: "#fff3cd",
                      color: "#856404",
                      px: 2,
                      py: 0.5,
                      fontSize: "13px",
                      fontWeight: 600,
                      borderRadius: "4px",
                      ...rubikfont,
                    }}
                  >
                    SAVE ₹{((productData.b2c_mrp || 0) - (productData.b2b_sellingPrice || 0)).toLocaleString()} ({discount}% OFF)
                  </Typography>
                )}
              </Box>

              {/* Stock & Quantity */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, flexWrap: "wrap" }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: productData.inStock ? "#2ecc71" : "#e74c3c" }} />
                  <Typography sx={{ fontSize: "14px", fontWeight: 600, color: productData.inStock ? "#2ecc71" : "#e74c3c", ...rubikfont }}>
                    {productData.inStock ? "IN STOCK" : "OUT OF STOCK"}
                  </Typography>
                  {/* <Typography sx={{ fontSize: "14px", color: "#999", ...rubikfont }}>
                    ({productData.availableQuantity} units available)
                  </Typography> */}
                </Box>

                {/* Quantity Selector */}
                {productData.inStock && (
                  <Box sx={{ mb: 3 }}>
                    <Typography sx={{ display: "block", fontSize: "13px", fontWeight: 600, mb: 1, textTransform: "uppercase", ...rubikfont }}>
                      Quantity
                    </Typography>
                    <Box sx={{ display: "inline-flex", alignItems: "center", border: "1px solid #000" }}>
                      <IconButton
                        onClick={() => setQuantity((q) => Math.max(1, Number(q || 1) - 1))}
                        sx={{ borderRight: "1px solid #000", borderRadius: 0, width: 44, height: 44 }}
                      >
                        <RemoveIcon />
                      </IconButton>
                      <Box sx={{ width: 80, textAlign: "center", fontWeight: 600, fontSize: "16px", ...rubikfont }}>
                        {quantity}
                      </Box>
                      <IconButton
                        onClick={() => setQuantity((q) => {
                          const max = Number(productData?.availableQuantity) || 9999;
                          const curr = Number(q || 1);
                          return Math.min(max, curr + 1);
                        })}
                        sx={{ borderLeft: "1px solid #000", borderRadius: 0, width: 44, height: 44 }}
                      >
                        <AddIcon />
                      </IconButton>
                    </Box>
                  </Box>
                )}

                {/* Action Buttons */}
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <Button
                    variant="contained"
                    startIcon={<ShoppingCartIcon />}
                    disabled={!productData.inStock}
                    sx={{
                      flex: 1,
                      backgroundColor: "#000",
                      color: "#fff",
                      py: { xs: 1.2, sm: 1.8 },
                      fontSize: { xs: "13px", sm: "15px" },
                      fontWeight: 600,
                      letterSpacing: "1px",
                      textTransform: "uppercase",
                      "&:hover": { backgroundColor: "#333" },
                      ...rubikfont,
                    }}
                    onClick={handleAddToCart}
                  >
                    {isInCart ? "Update Cart" : "Add to Cart"}
                  </Button>
                  <Button
                    variant="outlined"
                    disabled={!productData.inStock}
                    sx={{
                      flex: 1,
                      color: "#000",
                      borderColor: "#000",
                      py: { xs: 1.2, sm: 1.8 },
                      fontSize: { xs: "13px", sm: "15px" },
                      fontWeight: 600,
                      letterSpacing: "1px",
                      textTransform: "uppercase",
                      "&:hover": { backgroundColor: "#000", color: "#fff" },
                      ...rubikfont,
                    }}
                    onClick={handleBuyNow}
                  >
                    Buy Now
                  </Button>
                </Box>
              </Box>

              {/* Benefits */}
              <Grid container spacing={2} sx={{ pt: 3, borderTop: "1px solid #f0f0f0" }}>
                {[
                  { icon: <LocalShippingOutlinedIcon />, title: "Express Shipping", desc: "2-3 Days Delivery" },
                  { icon: <VerifiedUserOutlinedIcon />, title: "2 Year Warranty", desc: "Official Warranty" },
                  { icon: <LocalOfferOutlinedIcon />, title: "Best Price", desc: "Guaranteed" },
                ].map((item, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index} sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                    {item.icon}
                    <Box>
                      <Typography sx={{ fontSize: "13px", fontWeight: 600, ...rubikfont }}>{item.title}</Typography>
                      <Typography sx={{ fontSize: "12px", color: "#666", ...rubikfont }}>{item.desc}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Grid>
        </Grid>

        {/* Tabs Section */}
        <Box sx={{ mt: 10, px: { xs: 2, sm: 3, md: 4 } }}>
          <Box sx={{ borderBottom: "1px solid #e0e0e0", mb: 5 }}>
            <Tabs
              value={activeTab}
              onChange={(e, val) => setActiveTab(val)}
              variant="scrollable"
              scrollButtons
              allowScrollButtonsMobile
              TabIndicatorProps={{ style: { backgroundColor: "#000" } }}
              sx={{
                "& .MuiTab-root": {
                  fontWeight: 600,
                  fontSize: "15px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  color: "#999",
                  "&.Mui-selected": { color: "#000" },
                  ...rubikfont,
                },
              }}
            >
              {tabs.map((tab) => (
                <Tab key={tab.id} label={tab.label} value={tab.id} />
              ))}
            </Tabs>
          </Box>

          <Box sx={{ maxWidth: "900px", mx: "auto" }}>
            {/* DESCRIPTION */}
            {activeTab === "description" && (
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, fontSize: { xs: "20px", md: "24px" }, ...rubikfont }}>
                  Product Description
                </Typography>
                <Typography sx={{ fontSize: "16px", color: "#666", lineHeight: 1.8, ...rubikfont }}>
                  {productData.description}
                </Typography>
              </Box>
            )}

            {/* FEATURES */}
            {activeTab === "features" && (
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, fontSize: { xs: "20px", md: "24px" }, ...rubikfont }}>
                  Key Features
                </Typography>
                <Box
                  sx={{
                    '& ul': { paddingLeft: '20px' },
                    '& li': { marginBottom: '10px', fontSize: '15px', color: '#333', ...rubikfont }
                  }}
                  dangerouslySetInnerHTML={{ __html: productData.key_features }}
                />
              </Box>
            )}

            {/* HOW TO USE */}
            {activeTab === "howto" && (
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, fontSize: { xs: "20px", md: "24px" }, ...rubikfont }}>
                  How to Use
                </Typography>
                <Box
                  sx={{
                    '& ul': { paddingLeft: '20px' },
                    '& li': { marginBottom: '10px', fontSize: '15px', color: '#333', ...rubikfont }
                  }}
                  dangerouslySetInnerHTML={{ __html: productData.how_to_use }}
                />
              </Box>
            )}

            {/* DETAILS */}
            {activeTab === "details" && (
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, fontSize: { xs: "20px", md: "24px" }, ...rubikfont }}>
                  Product Details
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {[
                    { label: "Brand", value: productData.brand },
                    { label: "Ideal For", value: productData.ideal_for },
                    { label: "Applied For", value: productData.applied_for },
                    { label: "Container Type", value: productData.container_type },
                    { label: "Country of Origin", value: productData.country_of_orgin },
                    { label: "Color", value: productData.color },
                    { label: "Unit", value: `${productData.unitValue} ${productData.unitName}` },
                  ].map((item, idx) => (
                    item.value && (
                      <Box
                        key={idx}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          py: 2,
                          borderBottom: "1px solid #f0f0f0",
                        }}
                      >
                        <Typography sx={{ color: "#666", fontWeight: 500, fontSize: "15px", ...rubikfont }}>{item.label}</Typography>
                        <Typography sx={{ color: "#000", fontWeight: 600, fontSize: "15px", ...rubikfont }}>{item.value}</Typography>
                      </Box>
                    )
                  ))}
                </Box>
              </Box>
            )}

            {/* REVIEWS */}
            {activeTab === "reviews" && (
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, fontSize: { xs: "20px", md: "24px" }, ...rubikfont }}>
                  Customer Reviews
                </Typography>

                <Paper variant="outlined" sx={{ p: { xs: 2, sm: 4 }, mb: 5, backgroundColor: "#fafafa", textAlign: "center" }}>
                  <Typography sx={{ fontSize: { xs: "40px", md: "56px" }, fontWeight: 700, ...rubikfont }}>
                    {productData.productRating || "0.0"}
                  </Typography>
                  <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5, my: 1 }}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <StarIcon
                        key={star}
                        sx={{
                          fontSize: 20,
                          color: star <= Number(productData.productRating || 0) ? "#FFB800" : "#e0e0e0",
                        }}
                      />
                    ))}
                  </Box>
                  <Typography sx={{ fontSize: "13px", color: "#666", ...rubikfont }}>
                    Based on {productData.totalReviews || 0} reviews
                  </Typography>
                </Paper>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {productData.reviews && productData.reviews.length > 0 ? (
                    productData.reviews.map((review, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          pb: 4,
                          borderBottom: "1px solid #f0f0f0",
                          display: "flex",
                          flexDirection: { xs: "column", sm: "row" },
                          gap: 2,
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 48,
                            height: 48,
                            bgcolor: "#000",
                            color: "#fff",
                            fontWeight: 600,
                            fontSize: "18px",
                          }}
                        >
                          {review.name ? review.name.charAt(0) : "U"}
                        </Avatar>

                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
                            <Typography sx={{ fontWeight: 600, fontSize: "16px", ...rubikfont }}>{review.name || "User"}</Typography>
                          </Box>

                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5, flexWrap: "wrap" }}>
                            <Box sx={{ display: "flex" }}>
                              {[1, 2, 3, 4, 5].map((star) => (
                                <StarIcon
                                  key={star}
                                  sx={{
                                    fontSize: 16,
                                    color: star <= review.rating ? "#FFB800" : "#e0e0e0",
                                  }}
                                />
                              ))}
                            </Box>
                            {review.date && (
                              <Typography sx={{ fontSize: "13px", color: "#999", ...rubikfont }}>
                                {new Date(review.date).toLocaleDateString("en-US", {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                })}
                              </Typography>
                            )}
                          </Box>

                          <Typography sx={{ fontSize: "15px", color: "#666", lineHeight: 1.7, ...rubikfont }}>
                            {review.comment}
                          </Typography>
                        </Box>
                      </Box>
                    ))
                  ) : (
                    <Typography sx={{ textAlign: 'center', color: '#666' }}>No reviews yet.</Typography>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </Box>


     {/* Similar Products */}
      <Box sx={{ mt: 8 }}>
  <Typography
    sx={{
      fontSize: { xs: "20px", md: "24px" },
      fontWeight: 600,
      mb: 3,
      ...rubikfont,
    }}
  >
    Similar Products
  </Typography>

  {/* Grid Layout like old project */}
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: {
        xs: "repeat(2, 1fr)",   // 2 per row on mobile
        sm: "repeat(3, 1fr)",   // 3 per row on small screens
        md: "repeat(5, 1fr)",   // 5 per row on desktop
      },
      gap: { xs: 2, sm: 3, md: 4 },
      minHeight: "300px",
    }}
  >
    {similarProducts && similarProducts.length > 0 ? (
      similarProducts.map((item, index) => (
        <Box
          key={item._id || index}
          className="product-card"
          sx={{
            bgcolor: "white",
            borderRadius: 2,
            overflow: "hidden",
            transition: "all 0.3s ease",
            cursor: "pointer",
            border: "1px solid #f0f0f0",
            "&:hover": {
              transform: "translateY(-8px)",
              boxShadow: "0 8px 20px rgba(0,0,0,0.12)",
              "& .product-image": {
                transform: "scale(1.08)",
              },
              "& .action-icons": {
                transform: "translateY(-50%) translateX(0)",
                opacity: 1,
              },
            },
          }}
        >
          {/* Product Image */}
          <Box
            sx={{
              position: "relative",
              paddingTop: "90%",
              overflow: "hidden",
              bgcolor: "#f8f8f8",
            }}
          >
            <img
              src={
                item.product_images && item.product_images.length > 0
                  ? item.product_images[0]
                  : "https://via.placeholder.com/300"
              }
              alt={item.title}
              className="product-image"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                transition: "transform 0.4s ease",
              }}
            />

            {/* Hover Action Icons */}
            <Box
              className="action-icons"
              sx={{
                position: "absolute",
                right: 10,
                top: "40%",
                transform: "translateY(-50%) translateX(60px)",
                display: "flex",
                flexDirection: "column",
                gap: 1,
                opacity: 0,
                transition: "all 0.4s ease",
              }}
            >
              <IconButton
                sx={{
                  bgcolor: "white",
                  width: { xs: 35, sm: 40, md: 45 },
                  height: { xs: 35, sm: 40, md: 45 },
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  "&:hover": {
                    bgcolor: "#222",
                    color: "white",
                    transform: "scale(1.1)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                <FavoriteBorderIcon
                  sx={{ fontSize: { xs: 16, sm: 18, md: 20 } }}
                />
              </IconButton>

              <IconButton
                sx={{
                  bgcolor: "white",
                  width: { xs: 35, sm: 40, md: 45 },
                  height: { xs: 35, sm: 40, md: 45 },
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  "&:hover": {
                    bgcolor: "#222",
                    color: "white",
                    transform: "scale(1.1)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                <ShoppingCartOutlinedIcon
                  sx={{ fontSize: { xs: 16, sm: 18, md: 20 } }}
                />
              </IconButton>

              <IconButton
                onClick={() => {
                  navigate(`/product/${item._id}`);
                  window.scrollTo(0, 0);
                }}
                sx={{
                  bgcolor: "white",
                  width: { xs: 35, sm: 40, md: 45 },
                  height: { xs: 35, sm: 40, md: 45 },
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  "&:hover": {
                    bgcolor: "#222",
                    color: "white",
                    transform: "scale(1.1)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                <VisibilityOutlinedIcon
                  sx={{ fontSize: { xs: 16, sm: 18, md: 20 } }}
                />
              </IconButton>
            </Box>
          </Box>

          {/* Product Details */}
          <Box sx={{ p: { xs: 1.5, sm: 2.0, md: 2.5 } }}>
            <Typography
              sx={{
                fontSize: { xs: "14px", sm: "16px", md: "18px" },
                fontWeight: 500,
                color: "#222",
                ...rubikfont,
                minHeight: { xs: "36px", sm: "44px", md: "48px" },
                // If you still want ellipsis for long titles:
                // whiteSpace: "nowrap",
                // overflow: "hidden",
                // textOverflow: "ellipsis",
              }}
            >
              {item.title}
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                sx={{
                  fontSize: { xs: "16px", sm: "18px", md: "20px" },
                  fontWeight: 600,
                  color: "#222",
                  ...rubikfont,
                }}
              >
                ₹{item.b2b_sellingPrice || item.sellingPrice || 0}
              </Typography>

              {item.b2c_mrp && (
                <Typography
                  sx={{
                    fontSize: { xs: "13px", sm: "14px", md: "16px" },
                    color: "red",
                    textDecoration: "line-through",
                    ...rubikfont,
                  }}
                >
                  ₹{item.b2c_mrp}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      ))
    ) : (
      <Box
        sx={{
          gridColumn: "1 / -1",
          textAlign: "center",
          py: 4,
          ...rubikfont,
        }}
      >
        <Typography>No similar products found.</Typography>
      </Box>
    )}
  </Box>
      </Box>


      </Box>

      {/* Auth Modal */}
      <Dialog
        open={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 2,
            padding: 2,
            minWidth: '300px'
          }
        }}
      >
        <DialogTitle sx={{ ...rubikfont, fontWeight: 600, textAlign: 'center' }}>
          Sign In Required
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ ...rubikfont, textAlign: 'center', color: '#666' }}>
            Please sign in to add items to your cart.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 2 }}>
          <Button
            onClick={() => setAuthModalOpen(false)}
            sx={{ ...rubikfont, color: '#666' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => navigate('/signin')}
            sx={{
              ...rubikfont,
              bgcolor: '#000',
              '&:hover': { bgcolor: '#333' }
            }}
          >
            Sign In
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductDetails;